import { formatDistanceToNow } from "date-fns";
import { useMemo, useState } from "react";
import { ExternalLinkIcon } from "@heroicons/react/outline";
import { useLocation } from "react-router-dom";

import { convertActionsToContextMenuItems } from "../../../ui/Cards/Card/utils";
import { ContextMenuItems } from "../../../ui/ContextMenu/types";
import { Action, DropdownMenuVariant } from "../../../ui/DropdownMenu";
import { useContextualActions } from "../../hooks/useContextualActions";
import { useItemSelection } from "../../hooks/useItemSelection";
import { RegistryItem } from "../../types";
import { getItemLink } from "../../utils";
import { useSmartTableNavigationPrefetch } from "../../hooks/useSmartTableNavigationPrefetch";

import { EntityType } from "@/queries/folder-entities/types";
import { useUser } from "@/context/user-context";
import { withPreservedWorkspaceSearch } from "@/utils/requestLogUrlParams";

export const useListItemLogic = (
  item: RegistryItem,
  onItemClick?: (item: RegistryItem) => void,
) => {
  const workspaceId = useUser().activeWorkspaceId!;
  const location = useLocation();
  const { handleItemClick } = useItemSelection(onItemClick);
  const { contextMenuActions, isSelected: selected } = useContextualActions(item);
  const [isExpanded, setIsExpanded] = useState(false);

  const formatDate = (dateString?: string) => {
    if (!dateString) {
      return "-";
    }
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return "-";
    }
  };

  const link = useMemo(
    () => withPreservedWorkspaceSearch(getItemLink(item, workspaceId), location.search),
    [item, location.search, workspaceId],
  );
  const prefetchSmartTableNavigation = useSmartTableNavigationPrefetch({
    item,
    workspaceId,
  });

  const hasActionableItems = Boolean(contextMenuActions && contextMenuActions.length > 0);
  const additionalItems = hasActionableItems
    ? [{ id: "divider", isDivider: true }, ...(contextMenuActions || [])]
    : [];

  const dropdownMenuItems: Action<RegistryItem>[] = [
    {
      id: "open-in-new-tab",
      label: "Open in new tab",
      icon: ExternalLinkIcon,
      onClick: () => window.open(link, "_blank"),
      variant: DropdownMenuVariant.PRIMARY,
    },
    ...additionalItems,
  ];

  const isBatchAction = contextMenuActions?.some(
    action =>
      typeof action === "object" &&
      "id" in action &&
      (action.id === "batch-move" || action.id === "batch-delete"),
  );

  const contextMenuItems: ContextMenuItems<RegistryItem>[] = convertActionsToContextMenuItems(
    isBatchAction
      ? (contextMenuActions as Action<RegistryItem>[])
      : (dropdownMenuItems as Action<RegistryItem>[]),
  ) as ContextMenuItems<RegistryItem>[];

  const handleClick = (e: React.MouseEvent) => {
    if (onItemClick) {
      e.preventDefault();
      const wasHandled = handleItemClick(e, item);
      if (!wasHandled) {
        onItemClick(item);
      }
    } else {
      const wasHandled = handleItemClick(e, item);
      if (wasHandled) {
        e.preventDefault();
      }
    }
  };

  const handleExpandToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const isExpandable = item.type === EntityType.FOLDER;

  return {
    selected,
    isExpanded,
    isExpandable,
    link,
    contextMenuItems,
    dropdownMenuItems,
    formatDate,
    handleClick,
    handleExpandToggle,
    prefetchSmartTableNavigation,
  };
};
